import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, LayoutGrid, CalendarDays, GanttChartSquare, ChevronLeft, ChevronRight, Plus, Table2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { TASK_STATUSES, STATUS_STYLE, fmtDate } from "../lib/api";
import { Avatar, Card, PageHeader, PriorityPill, Segmented, StatusPill } from "../components/pm/bits";
import { Input } from "../components/ui/input";
import { MemberItems } from "../components/pm/MemberSelect";
import { QuickStatusButtons } from "../components/pm/StatusActions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const DAY = 86400000;
const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

function Kanban({ list, updateTask, openTask }) {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {TASK_STATUSES.map((s) => {
        const items = list.filter((t) => t.status === s);
        return (
          <div
            key={s}
            onDragOver={(e) => { e.preventDefault(); setOver(s); }}
            onDragLeave={() => setOver(null)}
            onDrop={() => { const t = list.find((x) => x.id === dragId); if (t && t.status !== s) updateTask(t.id, { status: s }); setDragId(null); setOver(null); }}
            className={`w-[320px] shrink-0 rounded-2xl p-2.5 transition-colors ${over === s ? "bg-[#f5efe3] ring-2 ring-[#b8975a]/50" : "bg-slate-100/70"}`}
            data-testid={`task-col-${s}`}
          >
            <div className="mb-2 flex items-center justify-between px-2 py-1.5">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-800"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_STYLE[s].dot }} /> {s}</span>
              <span className="rounded-md bg-white px-2 py-0.5 text-xs font-semibold text-slate-500">{items.length}</span>
            </div>
            <div className="max-h-[calc(100vh-290px)] space-y-2.5 overflow-y-auto pr-0.5">
              {items.map((t) => (
                <div
                  key={t.id}
                  draggable
                  onDragStart={() => setDragId(t.id)}
                  onClick={() => openTask(t.id)}
                  data-testid={`task-card-${t.id}`}
                  className={`cursor-pointer rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition-shadow hover:border-[#e7dcc6] hover:shadow-md ${dragId === t.id ? "opacity-50" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold leading-snug text-slate-900">{t.title}</p>
                    <PriorityPill priority={t.priority} />
                  </div>
                  <p className="mt-1 truncate text-xs text-slate-400">{t.project}</p>
                  <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2.5">
                    <span className={`text-xs ${t.overdue ? "font-semibold text-red-600" : "text-slate-400"}`}>{fmtDate(t.dueDate)}</span>
                    <div className="flex items-center gap-2">
                      <QuickStatusButtons task={t} />
                      <Avatar person={t.assigneeAvatar} size={24} />
                    </div>
                  </div>
                </div>
              ))}
              {items.length === 0 && <p className="px-2 py-6 text-center text-xs text-slate-400">Drop tasks here</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CalendarView({ list, openTask }) {
  const [cur, setCur] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const start = new Date(cur);
  start.setDate(1 - ((cur.getDay() + 6) % 7));
  const days = Array.from({ length: 42 }, (_, i) => new Date(start.getTime() + i * DAY + 3600000));
  const byDay = useMemo(() => {
    const m = {};
    list.forEach((t) => t.dueDate && (m[t.dueDate] = [...(m[t.dueDate] || []), t]));
    return m;
  }, [list]);
  const todayS = iso(new Date());
  return (
    <Card className="p-4 md:p-6" data-testid="tasks-calendar">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900">{cur.toLocaleString("en-GB", { month: "long", year: "numeric" })}</h3>
        <div className="flex gap-1">
          <button onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() - 1, 1))} className="rounded-lg border border-slate-200 p-2 hover:bg-slate-50" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => { const d = new Date(); setCur(new Date(d.getFullYear(), d.getMonth(), 1)); }} className="rounded-lg border border-slate-200 px-3 text-xs font-semibold hover:bg-slate-50">Today</button>
          <button onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() + 1, 1))} className="rounded-lg border border-slate-200 p-2 hover:bg-slate-50" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="grid min-w-[700px] grid-cols-7 overflow-hidden rounded-xl border border-slate-200">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="border-b border-slate-200 bg-slate-50 px-2 py-2 text-xs font-semibold text-slate-500">{d}</div>
          ))}
          {days.map((d, i) => {
            const k = iso(d);
            const items = byDay[k] || [];
            const inMonth = d.getMonth() === cur.getMonth();
            return (
              <div key={i} className={`min-h-[104px] border-b border-r border-slate-100 p-1.5 ${inMonth ? "bg-white" : "bg-slate-50/60"}`}>
                <p className={`mb-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${k === todayS ? "bg-[#00573f] font-bold text-white" : inMonth ? "text-slate-700" : "text-slate-300"}`}>{d.getDate()}</p>
                <div className="space-y-1">
                  {items.slice(0, 3).map((t) => (
                    <button key={t.id} onClick={() => openTask(t.id)} className="block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-medium" style={{ backgroundColor: STATUS_STYLE[t.status].bg, color: STATUS_STYLE[t.status].fg }} title={t.title}>
                      {t.title}
                    </button>
                  ))}
                  {items.length > 3 && <p className="px-1 text-[10px] font-semibold text-slate-400">+{items.length - 3} more</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function Timeline({ list, openTask, projects }) {
  const dated = list.filter((t) => t.startDate || t.dueDate);
  if (!dated.length) return <Card className="p-10 text-center text-sm text-slate-400">No dated tasks.</Card>;
  const ts = (s) => new Date(s + "T00:00:00").getTime();
  const min = Math.min(...dated.map((t) => ts(t.startDate || t.dueDate)));
  const max = Math.max(...dated.map((t) => ts(t.dueDate || t.startDate))) + DAY;
  const startW = min - (((new Date(min).getDay() + 6) % 7) * DAY);
  const weeks = Math.ceil((max - startW) / (7 * DAY));
  const W = 64;
  const groups = projects.map((p) => ({ p, items: dated.filter((t) => t.projectId === p.id) })).filter((g) => g.items.length);
  const todayX = ((Date.now() - startW) / (7 * DAY)) * W;
  return (
    <Card className="overflow-hidden" data-testid="tasks-timeline">
      <div className="overflow-x-auto">
        <div style={{ width: 280 + weeks * W }}>
          <div className="sticky top-0 z-10 flex border-b border-slate-200 bg-slate-50">
            <div className="w-[280px] shrink-0 px-4 py-2 text-xs font-semibold text-slate-500">Task</div>
            {Array.from({ length: weeks }, (_, i) => (
              <div key={i} className="shrink-0 border-l border-slate-200 px-1 py-2 text-center text-[10px] font-semibold text-slate-400" style={{ width: W }}>
                {new Date(startW + i * 7 * DAY).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
              </div>
            ))}
          </div>
          <div className="relative">
            {todayX > 0 && todayX < weeks * W && <div className="pointer-events-none absolute bottom-0 top-0 z-[1] w-0.5 bg-[#b8975a]" style={{ left: 280 + todayX }} />}
            {groups.map(({ p, items }) => (
              <div key={p.id}>
                <div className="flex border-b border-slate-100 bg-[#fbf8f1]">
                  <div className="w-[280px] shrink-0 truncate px-4 py-2 text-xs font-bold text-[#00573f]">{p.name}</div>
                </div>
                {items.map((t) => {
                  const s = ts(t.startDate || t.dueDate), e = ts(t.dueDate || t.startDate) + DAY;
                  const left = ((s - startW) / (7 * DAY)) * W, width = Math.max(((e - s) / (7 * DAY)) * W, 8);
                  return (
                    <div key={t.id} className="flex items-center border-b border-slate-50 hover:bg-slate-50">
                      <button onClick={() => openTask(t.id)} className="w-[280px] shrink-0 truncate px-4 py-2 text-left text-xs text-slate-700 hover:text-[#00573f]">{t.title}</button>
                      <div className="relative h-8" style={{ width: weeks * W }}>
                        <button
                          onClick={() => openTask(t.id)}
                          title={`${t.title} · ${fmtDate(t.startDate)} → ${fmtDate(t.dueDate)}`}
                          className="absolute top-1.5 h-5 rounded-md shadow-sm transition-opacity hover:opacity-80"
                          style={{ left, width, backgroundColor: t.status === "Not Started" ? (t.overdue ? "#f87171" : "#00573f") : STATUS_STYLE[t.status].dot }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function Tasks() {
  const { tasks, user, updateTask, openTask, projects, team, openDialog } = useApp();
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [scope, setScope] = useState(params.get("mine") ? "mine" : "all");
  const [view, setView] = useState("kanban");
  const [proj, setProj] = useState("all");
  const [who, setWho] = useState("all");

  const mine = tasks.filter((t) => t.assigneeId === user?.id);
  const list = (scope === "mine" ? mine : tasks).filter(
    (t) => (proj === "all" || t.projectId === proj) && (who === "all" || t.assigneeId === who) && `${t.title} ${t.project}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="mx-auto max-w-7xl animate-fade-in-up">
      <PageHeader title="Tasks" subtitle={`${tasks.length} live tasks · click any task to view, edit & comment`}>
        <button onClick={() => openDialog("task")} className="flex h-9 items-center gap-1.5 rounded-lg bg-[#00573f] px-3 text-sm font-semibold text-white hover:bg-[#004330]" data-testid="add-task-btn">
          <Plus className="h-4 w-4" /> New Task
        </button>
      </PageHeader>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="tasks-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks..." className="h-9 bg-white pl-9" />
        </div>
        <Segmented testid="tasks-scope" value={scope} onChange={setScope} options={[{ value: "all", label: "All tasks" }, { value: "mine", label: `My tasks (${mine.length})` }]} />
        <Select value={proj} onValueChange={setProj}>
          <SelectTrigger className="h-9 w-48 bg-white text-xs" data-testid="tasks-project-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={who} onValueChange={setWho}>
          <SelectTrigger className="h-9 w-40 bg-white text-xs" data-testid="tasks-assignee-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Everyone</SelectItem>
            <MemberItems team={team} />
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <Segmented
            testid="tasks-view"
            value={view}
            onChange={setView}
            options={[
              { value: "kanban", label: "Kanban", icon: LayoutGrid },
              { value: "table", label: "Table", icon: Table2 },
              { value: "calendar", label: "Calendar", icon: CalendarDays },
              { value: "timeline", label: "Timeline", icon: GanttChartSquare },
            ]}
          />
        </div>
      </div>
      {view === "kanban" && <Kanban list={list} updateTask={updateTask} openTask={openTask} />}
      {view === "calendar" && <CalendarView list={list} openTask={openTask} />}
      {view === "timeline" && <Timeline list={list} openTask={openTask} projects={projects} />}
      {view === "table" && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="tasks-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Task", "Project", "Phase", "Assigned To", "Start", "Finish", "Priority", "Status", ""].map((h) => (
                    <TableHead key={h} className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((t) => (
                  <TableRow key={t.id} className="cursor-pointer hover:bg-[#fbf8f1]" onClick={() => openTask(t.id)}>
                    <TableCell className="min-w-[220px] font-medium text-slate-900">{t.title}</TableCell>
                    <TableCell className="min-w-[160px] text-sm text-slate-600">{t.project}</TableCell>
                    <TableCell className="text-sm text-slate-600">{t.phase || "–"}</TableCell>
                    <TableCell><div className="flex items-center gap-2 whitespace-nowrap"><Avatar person={t.assigneeAvatar} size={24} /><span className="text-sm">{t.assignee || "–"}</span></div></TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-slate-600">{fmtDate(t.startDate)}</TableCell>
                    <TableCell className={`whitespace-nowrap text-sm ${t.overdue ? "font-semibold text-red-600" : "text-slate-600"}`}>{fmtDate(t.dueDate)}</TableCell>
                    <TableCell><PriorityPill priority={t.priority} /></TableCell>
                    <TableCell><StatusPill status={t.status} /></TableCell>
                    <TableCell><QuickStatusButtons task={t} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {list.length === 0 && <p className="p-8 text-center text-sm text-slate-400">No tasks found.</p>}
        </Card>
      )}
    </div>
  );
}
